import { useParams, useNavigate } from 'react-router';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useToolDefinitions } from '@/hooks';
import { INTEGRATIONS } from '@/modules/integrations';
import { IntegrationActionCard } from '@/modules/integrations/components/integration-action-card';
import type { IntegrationType } from '@/modules/integrations/types';

/** Actions available for one integration's provider, each linking to its usage detail page. */
export default function IntegrationActionsPage() {
	const { provider } = useParams<{ provider: string }>();
	const navigate = useNavigate();
	const integration = provider ? INTEGRATIONS[provider as IntegrationType] : undefined;
	const { data: tools, isLoading, isError } = useToolDefinitions();
	const actions = (tools ?? []).filter((tool) => tool.provider.toLowerCase() === provider?.toLowerCase());

	return (
		<div className='flex flex-col h-full'>
			<div className='sticky top-0 z-10 bg-white flex shrink-0 items-center gap-2 px-4 h-[60px] border-b border-gray-500'>
				<Button
					variant='ghost'
					size='icon'
					className='size-7 rounded-full'
					onClick={() => navigate('/settings/integrations')}
				>
					<ChevronLeft size={16} />
				</Button>
				<h1 className='text-base font-semibold'>{integration?.name ?? provider} actions</h1>
			</div>

			<div className='flex flex-1 min-h-0 flex-col gap-3 p-6 overflow-auto'>
				{isLoading ? (
					Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className='h-16 rounded-xl' />)
				) : isError ? (
					<div className='text-sm text-red-500 text-center py-10'>Failed to load actions.</div>
				) : !actions.length ? (
					<div className='text-sm text-muted-foreground text-center py-10'>
						No actions available for this integration yet.
					</div>
				) : (
					actions.map((action) => (
						<IntegrationActionCard
							key={action.id}
							provider={action.provider}
							tool={action.tool}
							label={action.label}
							description={action.description}
							riskLevel={action.riskLevel}
							onSelect={() => navigate(`/settings/integrations/${provider}/actions/${action.tool}`)}
						/>
					))
				)}
			</div>
		</div>
	);
}
